import { useState } from 'react'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import type {
  GridSelection,
  Row,
  TimeGridEntity,
  TimeGridEvent,
  TimeSlot,
} from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'
import {
  fromCellKey,
  toCellKey,
} from 'modules/shift_management/utils/TimeGridTable/cellCoordinates'
import {
  useHeaderDragSelection,
  type GridSelectionState,
  type HeaderDragAxis,
  type SelectionModifiers,
} from 'modules/shift_management/hooks/gridTable/selection/useHeaderDragSelection'
import { useEventSelection } from 'modules/shift_management/hooks/gridTable/selection/useEventSelection'
import {
  buildCellRangeSelection,
  buildColumnCellsExceptSelection,
  buildColumnRangeKeys,
  buildColumnSelection,
  buildRowCellsExceptSelection,
  buildRowRangeKeys,
  buildSelectedColumnIndexes,
  buildSelectedRowIds,
  buildSingleCellSelection,
  toggleCellSelection,
} from 'modules/shift_management/utils/TimeGridTable/selection/builds'
import {
  areSetsEqual,
  isCellSelectable as checkIsCellSelectable,
  countSelectableRows,
  isRowSelectable,
} from 'modules/shift_management/utils/TimeGridTable/selection/checks'

interface UseGridSelectionProps<TEntity extends TimeGridEntity, TEvent extends TimeGridEvent> {
  rows: Row<TEntity, TEvent>[]
  slots: TimeSlot[]
  rowIndexByEntityId: Map<string, number>
  isCellSelectable?: (entity: TEntity, slot: TimeSlot) => boolean
}

function useGridSelection<TEntity extends TimeGridEntity, TEvent extends TimeGridEvent>({
  rows,
  slots,
  rowIndexByEntityId,
  isCellSelectable,
}: UseGridSelectionProps<TEntity, TEvent>): {
  selection: GridSelection
  selectedCellKeys: Set<string>
  selectedRowIds: Set<string> | null
  selectedColumnIndexes: Set<number> | null
  selectedEventKeys: Set<string>
  clearSelection: () => void
  handleCellSelected: (entity: TEntity, slotIndex: number, modifiers: SelectionModifiers) => void
  handleRowHeaderSelected: (entity: TEntity, modifiers?: SelectionModifiers) => void
  handleColumnHeaderSelected: (columnIndex: number, modifiers: SelectionModifiers) => void
  handleHeaderDragStart: (params: {
    axis: HeaderDragAxis
    index: number
    modifiers: SelectionModifiers
    isPrimaryButtonPressed?: boolean
  }) => void
  handleEventSelected: (entityId: string, eventId: string, modifiers: SelectionModifiers) => void
  handleCornerCellSelected: () => void
  isAllSelected: boolean
  fromCellKey: (key: string) => { entityId: string; slotIndex: number }
} {
  const [gridState, setGridState] = useState<GridSelectionState>({
    selection: { kind: 'none' },
    rowAnchor: null,
    columnAnchor: null,
    suppressedHeaderClickAxis: null,
    headerPress: null,
  })
  const { selection, headerPress } = gridState

  const { handleHeaderDragStart } = useHeaderDragSelection({
    rows,
    headerPress,
    setGridState,
  })

  const selectableRowCount = useUnsafeMemo(() => countSelectableRows(rows), [rows])
  const canSelectAll = selectableRowCount > 0 && slots.length > 0

  const isAllSelected = selection.kind === 'all'

  const setSelection = useUnsafeCallback(
    (nextSelection: GridSelection | ((prev: GridSelection) => GridSelection)) => {
      setGridState((prev) => ({
        ...prev,
        selection:
          typeof nextSelection === 'function' ? nextSelection(prev.selection) : nextSelection,
      }))
    },
    []
  )

  const checkSelectable = useUnsafeCallback(
    (entityId: string): boolean => {
      const rowIndex = rowIndexByEntityId.get(entityId)
      const row = rowIndex === undefined ? undefined : rows[rowIndex]

      return isRowSelectable(row)
    },
    [rows, rowIndexByEntityId]
  )

  const { selectedEventKeys, handleEventSelected } = useEventSelection({
    selection,
    checkSelectable,
    setSelection,
  })

  const checkCellSelectable = useUnsafeCallback(
    (entityId: string, slotIndex: number): boolean => {
      return checkIsCellSelectable(
        entityId,
        slotIndex,
        rows,
        slots,
        rowIndexByEntityId,
        isCellSelectable
      )
    },
    [isCellSelectable, rowIndexByEntityId, rows, slots]
  )

  const selectedCellKeys = useUnsafeMemo(() => {
    return selection.kind === 'cells' ? new Set(selection.keys) : new Set<string>()
  }, [selection])

  const selectedRowIds = useUnsafeMemo(() => buildSelectedRowIds({ selection }), [selection])

  const selectedColumnIndexes = useUnsafeMemo(
    () => buildSelectedColumnIndexes(selection),
    [selection]
  )

  const clearSelection = useUnsafeCallback(() => {
    setGridState((prev) => ({
      ...prev,
      selection: { kind: 'none' },
      rowAnchor: null,
      columnAnchor: null,
    }))
  }, [])

  const handleCellSelected = useUnsafeCallback(
    (
      entity: TEntity,
      slotIndex: number,
      modifiers: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }
    ) => {
      if (!checkCellSelectable(entity.id, slotIndex)) return

      const cell = { entityId: entity.id, slotIndex }
      const isMetaPressed = modifiers.metaKey || modifiers.ctrlKey
      const anchorCell = selection.kind === 'cells' ? selection.anchor : null

      if (modifiers.shiftKey && !anchorCell) {
        setSelection(buildSingleCellSelection(cell))
        return
      }

      if (modifiers.shiftKey && anchorCell) {
        setSelection((prev) =>
          buildCellRangeSelection({
            anchorCell,
            targetCell: cell,
            append: isMetaPressed,
            previousSelection: prev,
            rows,
            rowIndexByEntityId,
            isCellSelectable: checkCellSelectable,
          })
        )
        return
      }

      if (isMetaPressed && (selectedRowIds?.has(entity.id) ?? false)) {
        setSelection(
          buildRowCellsExceptSelection({
            rowIds: selectedRowIds ?? new Set<string>(),
            excludedCell: cell,
            slots,
            isCellSelectable: checkCellSelectable,
          })
        )
        return
      }

      if (isMetaPressed && (selectedColumnIndexes?.has(slotIndex) ?? false)) {
        setSelection(
          buildColumnCellsExceptSelection({
            columnIndexes: selectedColumnIndexes ?? new Set<number>(),
            excludedCell: cell,
            rows,
            isCellSelectable: checkCellSelectable,
          })
        )
        return
      }

      if (isMetaPressed) {
        setSelection((prev) => toggleCellSelection(prev, cell))
        return
      }

      if (selection.kind === 'cells' && selection.keys.size === 1) {
        const key = toCellKey(entity.id, slotIndex)
        if (selection.keys.has(key)) {
          clearSelection()
          return
        }
      }

      setSelection(buildSingleCellSelection(cell))
    },
    [
      checkCellSelectable,
      clearSelection,
      rowIndexByEntityId,
      rows,
      selection,
      selectedColumnIndexes,
      selectedRowIds,
      setSelection,
      slots,
    ]
  )

  const handleRowHeaderSelected = useUnsafeCallback(
    (
      entity: TEntity,
      modifiers: SelectionModifiers = {
        shiftKey: false,
        metaKey: false,
        ctrlKey: false,
      }
    ) => {
      const rowIndex = rowIndexByEntityId.get(entity.id)
      if (rowIndex === undefined) return

      const row = rows[rowIndex]
      if (!row || !isRowSelectable(row)) return
      const rowId = row.entity.id

      setGridState((prev) => {
        if (prev.suppressedHeaderClickAxis === 'row') {
          return {
            ...prev,
            suppressedHeaderClickAxis: null,
          }
        }

        if (modifiers.shiftKey && prev.rowAnchor !== null) {
          const startIndex = Math.min(prev.rowAnchor, rowIndex)
          const endIndex = Math.max(prev.rowAnchor, rowIndex)

          if (startIndex === endIndex) {
            if (prev.selection.kind === 'row' && prev.selection.rowId === rowId) {
              return {
                ...prev,
                selection: { kind: 'none' },
                rowAnchor: null,
              }
            }

            return {
              ...prev,
              selection: { kind: 'row', rowId },
              rowAnchor: rowIndex,
            }
          }

          return {
            ...prev,
            selection: { kind: 'rowRange', keys: buildRowRangeKeys(startIndex, endIndex, rows) },
          }
        }

        const isMetaPressed = modifiers.metaKey || modifiers.ctrlKey

        if (isMetaPressed) {
          const selectedRows =
            buildSelectedRowIds({ selection: prev.selection }) ?? new Set<string>()

          if (selectedRows.has(rowId)) {
            selectedRows.delete(rowId)
          } else {
            selectedRows.add(rowId)
          }

          if (selectedRows.size === 0) {
            return {
              ...prev,
              selection: { kind: 'none' },
              rowAnchor: null,
            }
          }

          if (selectedRows.size === 1) {
            const [selectedRowId] = selectedRows

            return {
              ...prev,
              selection: { kind: 'row', rowId: selectedRowId ?? rowId },
              rowAnchor: rowIndex,
            }
          }

          return {
            ...prev,
            selection: { kind: 'rowRange', keys: selectedRows },
            rowAnchor: prev.rowAnchor ?? rowIndex,
          }
        }

        if (prev.selection.kind === 'row' && prev.selection.rowId === rowId) {
          return {
            ...prev,
            selection: { kind: 'none' },
            rowAnchor: null,
          }
        }

        return {
          ...prev,
          selection: { kind: 'row', rowId },
          rowAnchor: rowIndex,
        }
      })
    },
    [rowIndexByEntityId, rows]
  )

  const handleColumnHeaderSelected = useUnsafeCallback(
    (columnIndex: number, modifiers: SelectionModifiers) => {
      setGridState((prev) => {
        if (prev.suppressedHeaderClickAxis === 'column') {
          return {
            ...prev,
            suppressedHeaderClickAxis: null,
          }
        }

        if (modifiers.shiftKey && prev.columnAnchor !== null) {
          const startIndex = Math.min(prev.columnAnchor, columnIndex)
          const endIndex = Math.max(prev.columnAnchor, columnIndex)

          if (startIndex !== endIndex) {
            return {
              ...prev,
              selection: { kind: 'columnRange', keys: buildColumnRangeKeys(startIndex, endIndex) },
            }
          }
        }

        const columnSelection = buildColumnSelection({
          columnIndex,
          rows,
          selectableRowCount,
          isCellSelectable: checkCellSelectable,
        })
        if (!columnSelection) return prev

        const isMetaPressed = modifiers.metaKey || modifiers.ctrlKey

        if (isMetaPressed && columnSelection.selectsWholeColumn) {
          const selectedColumns = buildSelectedColumnIndexes(prev.selection) ?? new Set<number>()

          if (selectedColumns.has(columnIndex)) {
            selectedColumns.delete(columnIndex)
          } else {
            selectedColumns.add(columnIndex)
          }

          if (selectedColumns.size === 0) {
            return {
              ...prev,
              selection: { kind: 'none' },
              columnAnchor: null,
            }
          }

          if (selectedColumns.size === 1) {
            const [selectedColumnIndex] = selectedColumns

            return {
              ...prev,
              selection: { kind: 'column', columnIndex: selectedColumnIndex ?? columnIndex },
              columnAnchor: selectedColumnIndex ?? columnIndex,
            }
          }

          return {
            ...prev,
            selection: { kind: 'columnRange', keys: selectedColumns },
            columnAnchor: prev.columnAnchor ?? columnIndex,
          }
        }

        if (columnSelection.selectsWholeColumn) {
          if (prev.selection.kind === 'column' && prev.selection.columnIndex === columnIndex) {
            return {
              ...prev,
              selection: { kind: 'none' },
              columnAnchor: null,
            }
          }

          return {
            ...prev,
            selection: columnSelection.selection,
            columnAnchor: columnIndex,
          }
        }

        if (
          prev.selection.kind === 'cells' &&
          columnSelection.selection.kind === 'cells' &&
          areSetsEqual(prev.selection.keys, columnSelection.selection.keys)
        ) {
          return {
            ...prev,
            selection: { kind: 'none' },
            columnAnchor: null,
          }
        }

        return {
          ...prev,
          selection: columnSelection.selection,
          columnAnchor: columnIndex,
        }
      })
    },
    [checkCellSelectable, rows, selectableRowCount]
  )

  const handleCornerCellSelected = useUnsafeCallback(() => {
    setSelection((prev) => {
      if (!canSelectAll) return { kind: 'none' }
      return prev.kind === 'all' ? { kind: 'none' } : { kind: 'all' }
    })
  }, [canSelectAll, setSelection])

  return {
    selection,
    selectedCellKeys,
    selectedRowIds,
    selectedColumnIndexes,
    selectedEventKeys,
    clearSelection,
    handleCellSelected,
    handleRowHeaderSelected,
    handleColumnHeaderSelected,
    handleHeaderDragStart,
    handleCornerCellSelected,
    isAllSelected,
    handleEventSelected,
    fromCellKey,
  }
}

export { useGridSelection }
